
import { Params } from '@angular/router';
import { SelectItem, SelectItemType } from '../components/select-with-search/select-item.type';
import { SelectionHelper } from '../components/select-with-search/selection';
import { FilterDefinition, FilterFixedValue, FilterItem, FilterItemType, FilterItemValue } from './filter-definition.type';

export type FilterSelectionMap = Map<string, { items: SelectItem[]; fixedItems: SelectItem[]; selection: string | string[]; defaultValue: string | string[] | undefined; selectAllLabel?: string; sortItemsByLabel?: boolean }>;

export class FilteringHelper {
  static readonly AllItemsId = 'all';

  static createFilterSelectionMap(filterDefinition: FilterDefinition): FilterSelectionMap {
    const map: FilterSelectionMap = new Map();
    filterDefinition.filters.forEach(f => {
      if (f.type !== FilterItemType.Select && f.type !== FilterItemType.MultiSelect) {
        return;
      }
      const items = f.items.map(v => this.convertToSelectItem(v));
      const fixedItems = f.fixedItems == null ? [] : f.fixedItems.map(v => this.convertToSelectFixedItem(v));
      const selection = this.getSelection(f, items.concat(fixedItems));
      const defaultValue = this.getDefaultSelection(f, items.concat(fixedItems));
      const selectAllLabel = f.selectAllLabel;
      const sortItemsByLabel = f.sortItemsByLabel ?? true;
      map.set(f.name, { items, fixedItems, selection, defaultValue, selectAllLabel, sortItemsByLabel });
    });

    return map;
  }

  static getFilterValueWithFallback(
    queryParams: Params,
    storedFilters: Record<string, string>,
    queryParamName: string,
    defaultValue?: string,
  ): string | undefined {
    const queryParamValue = queryParams[queryParamName];
    const storedValue = storedFilters[queryParamName];

    return (typeof queryParamValue === 'string' ? queryParamValue : undefined)
      ?? (typeof storedValue === 'string' ? storedValue : undefined)
      ?? defaultValue;
  }

  private static convertToSelectItem(filterValue: FilterItemValue): SelectItem {
    if (filterValue == null) {
      return null;
    }

    return {
      id: filterValue.id,
      label: filterValue.label + (filterValue.count != null ? ` (${filterValue.count})` : ''),
      group: filterValue.group,
      disabled: filterValue.disabled,
      description: filterValue.description,
    } as SelectItem;
  }

  private static convertToSelectFixedItem(fixedItem: FilterFixedValue): SelectItem {
    return fixedItem != null ? SelectionHelper.createFixedItem(fixedItem.id, fixedItem.label, fixedItem.type) : null;
  }

  private static getSelection(filter: FilterItem, items: SelectItem[]): string | string[] {
    if (filter.type === FilterItemType.Select) {
      const filterValueId: string | string[] = (filter.value as string | string[] | undefined) ?? this.AllItemsId;
      const selectedItem = items.find(i => this.compareFilterValues(i.id, filterValueId));
      return selectedItem?.id ?? filterValueId;
    }

    if (filter.type === FilterItemType.MultiSelect) {
      if (filter.value != null && Array.isArray(filter.value) && filter.value.length > 0) {
        return filter.value as string[];
      }

      // Check if allowEmptyArray is enabled
      const multiSelectFilter = filter;
      if (multiSelectFilter.allowEmptyArray) {
        // Show placeholder (no selection)
        return [];
      }

      // Select all items
      const regularItems = items.filter(item => item.type !== SelectItemType.All);
      return regularItems.map(item => item.id);
    }

    throw new Error(`Filter type ${filter.type} is not supported`);
  }

  private static getDefaultSelection(filter: FilterItem, items: SelectItem[]): string | string[] | undefined {
    if (filter.type === FilterItemType.Select) {
      // Explicitly check if defaultValue is undefined - if so, return undefined to exclude from clear operations
      if (filter.defaultValue === undefined) {
        return undefined;
      }

      const filterValueId = filter.defaultValue ?? this.AllItemsId;
      const selectedItem = items.find(i => this.compareFilterValues(i.id, filterValueId));
      return selectedItem?.id ?? filter.defaultValue ?? this.AllItemsId;
    }

    if (filter.type === FilterItemType.MultiSelect) {
      // Explicitly check if defaultValue is undefined - if so, return undefined to exclude from clear operations
      if (filter.defaultValue === undefined) {
        return undefined;
      }

      if (filter.defaultValue != null && Array.isArray(filter.defaultValue) && filter.defaultValue.length > 0) {
        return filter.defaultValue as string[];
      }

      // Check if allowEmptyArray is enabled
      const multiSelectFilter = filter;
      if (multiSelectFilter.allowEmptyArray) {
        // Show placeholder (no selection)
        return [];
      }

      // Select all items
      const regularItems = items.filter(item => item.type !== SelectItemType.All);
      return regularItems.map(item => item.id);
    }

    throw new Error(`Filter type ${filter.type} is not supported`);
  }

  private static compareFilterValues(itemId: any, filterValue: any): boolean {
    // Handle null/undefined cases
    if (itemId == null && filterValue == null) {
      return true;
    }
    if (itemId == null || filterValue == null) {
      return false;
    }

    // Direct comparison first (most efficient for same types)
    if (itemId === filterValue) {
      return true;
    }

    // This handles boolean/string/number mixed types properly
    const itemIdStr = String(itemId);
    const filterValueStr = String(filterValue);

    return itemIdStr === filterValueStr;
  }
}
